// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: b022d981-dec6-4b5b-9ac7-06780b030efe

// ---------------------------------------------------------------------------
// Data store — the single source of truth abstraction.
//
// Today: browser localStorage, seeded from seed.js. Every method is async and
// tenant-scoped, so swapping to the AWS API (API Gateway + Lambda + DynamoDB)
// is a drop-in: replace the bodies with fetch() calls, keep the signatures.
//
//   DynamoDB single-table target:
//     PK = TENANT#<tenantId>              SK = FS#<factSheetId>
//     PK = TENANT#<tenantId>              SK = FIELDDEF#<type>#<key>   (custom fields)
//     GSI1: type + name for inventory listing/sort
// ---------------------------------------------------------------------------

import { SEED_FACT_SHEETS, TENANTS, CATALOG } from './seed';
import imported from './imported.json';
import { allFieldsFor, schemaFor, FIELD_TYPES, registerFieldDefs } from './schema';
import { getConfig } from './config';
import { getIdToken } from './auth';

const LEGACY_LS_KEY = 'eams.cmdb.v3';
const LS_PREFIX = 'eams.cmdb.v4';
const MANIFEST_KEY = `${LS_PREFIX}.manifest`;
const chunkKey = (tenantId) => `${LS_PREFIX}.tenant.${tenantId}`;

// Real client data imported from Optimum engagement exports (NGHS LeanIX export,
// DFCI hosting/auth). Merge with the curated seed; curated hero fact sheets win
// over any imported duplicate (dedupe by tenant + name).
const ALL_TENANTS = [...TENANTS, ...(imported.tenants || [])];
const seededKeys = new Set(SEED_FACT_SHEETS.map((f) => f.tenantId + '|' + f.name.toLowerCase()));
const MERGED_FACT_SHEETS = [
  ...SEED_FACT_SHEETS,
  ...(imported.factSheets || []).filter((f) => !seededKeys.has(f.tenantId + '|' + f.name.toLowerCase())),
];

// ---- Persistence: DynamoDB via the local API, with localStorage fallback ----
// Reads come from an in-memory cache hydrated from the API on startup (falls
// back to localStorage / seed if the API is down). Writes update the cache +
// localStorage immediately and write-through to the API (DynamoDB) in the
// background — responsive, and never breaks when the API is off.
const apiBase = () => getConfig().apiUrl || 'http://localhost:4000';
// Every API call carries the Cognito ID token when we have one; the API
// independently verifies it and scopes the response to the caller's tenant.
const authHeaders = () => {
  const t = getIdToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const jsonHeaders = () => ({ 'Content-Type': 'application/json', ...authHeaders() });
let cache = null;
let apiUp = false;

// ---- Stored rows are immutable ---------------------------------------------
// `listFactSheets` used to hand every caller a full JSON deep clone of every
// row so that nothing downstream could reach back and corrupt the store. That
// is the right guarantee and the wrong way to buy it: it cost a 4,000-row deep
// clone on each of 47 call sites, once per navigation, forever.
//
// Freezing the stored rows buys the same guarantee once. Nothing here mutates a
// row in place — every write path builds a new object and swaps it in — so the
// freeze costs nothing we were relying on, and callers can now be handed a
// cheap shallow copy instead. The nested objects (`fields`, `tags`,
// `relations`, `integrations`) are shared rather than copied, and frozen: a
// screen that tried `row.fields.owner = …` used to silently edit its private
// copy, and now throws, which is the honest answer either way — that write
// never reached the store and never will. Screens that genuinely need a mutable
// record already take their own copy (`SnapshotCompare.cloneApp`,
// `ServerMapping`'s `deep`), and a copy of a frozen object is not frozen.
function deepFreeze(v) {
  if (!v || typeof v !== 'object' || Object.isFrozen(v)) return v;
  Object.freeze(v);
  Object.keys(v).forEach((k) => deepFreeze(v[k]));
  return v;
}

// Called wherever rows enter the cache. Already-frozen rows cost one
// Object.isFrozen check, so re-running it over the whole list is free.
function freezeRows(db) {
  if (db && Array.isArray(db.factSheets)) db.factSheets.forEach(deepFreeze);
  return db;
}

// ---- Persistence layout (H8) ------------------------------------------------
// The API (DynamoDB via the Lambda) is the source of truth — `initStore` always
// refetches a workspace from it. localStorage is only an offline convenience: a
// warm cache so the app has something to render before/without the network.
// That is the decision degraded mode leans on below: it is CORRECT to stop
// writing a workspace to localStorage when it does not fit, and keep serving it
// from the in-memory `cache` for the rest of the session, rather than either
// losing data or silently going stale.
//
// One workspace used to mean one write: `save()` `JSON.stringify`d the ENTIRE
// database — every tenant's fact sheets — on every single call, so editing one
// field in one client's record rewrote every other client's data too. At one
// 7,305-server workspace (10-15MB) that is most of the ~5MB-10MB quota by
// itself; a second client on the same browser (a master account, an Optimum
// tester) tips it over, `setItem` throws, the catch swallows it, and every
// write for every tenant silently stops landing — including small workspaces
// that would have fit fine on their own.
//
// The fix is to make a write's cost proportional to what changed:
//   eams.cmdb.v4.manifest          -> { tenantIds, catalog, seededAt }
//   eams.cmdb.v4.tenant.<tenantId> -> { factSheets, fieldDefs }  (that tenant only)
// A save that names its tenant (saveFactSheet, deleteFactSheet, addFieldDef all
// know whose row they touched) now does ONE setItem, for that tenant's chunk
// only — never another tenant's. The manifest is only rewritten when the set of
// persisted tenants actually changes (a new one appears, or one drops out after
// a quota failure), not on every save.
const knownTenantIds = new Set();
// tenantId -> reason string. Non-empty means that tenant's fact sheets are
// living in memory only for this session — set once, on a persistence failure,
// and read by `persistenceDegraded()` so the app can say so instead of quietly
// serving (or silently dropping) stale local data. Cleared the moment a later
// write for that same tenant fits and succeeds.
const degraded = new Map();

function isQuotaError(e) {
  if (!e) return false;
  if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;
  if (e.code === 22 || e.code === 1014) return true; // legacy Chrome / Firefox codes
  return /quota/i.test(e.message || '');
}

function readJSON(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

// Batch mode (ImportDialog H8): while a tenant is batching, `save()` still
// updates the in-memory cache immediately (so every read in the same import —
// progress, re-plan, the result panel — sees the latest rows) but skips the
// localStorage write. `endPersistBatch` flushes once. Scoped per tenant so an
// unrelated screen open in another tab/tenant is never held up by someone
// else's import.
const batchingTenants = new Set();
const dirtyBatchedTenants = new Set();

export function beginPersistBatch(tenantId) {
  if (tenantId) batchingTenants.add(tenantId);
}

export function endPersistBatch(tenantId) {
  if (!tenantId) return;
  batchingTenants.delete(tenantId);
  if (dirtyBatchedTenants.has(tenantId) && cache) {
    dirtyBatchedTenants.delete(tenantId);
    persistTenant(cache, tenantId);
    reconcileManifestIfNeeded(cache, [tenantId]);
  }
}

// Exposes the one thing the UI needs to know: is anything NOT actually saved
// to this browser right now, and why. Deliberately a plain read of in-memory
// state (no storage access), so it is cheap enough to poll and it is
// impossible for it to itself trip the quota it is reporting on.
export function persistenceDegraded() {
  if (degraded.size === 0) return { degraded: false, tenants: [], reason: null };
  const tenants = [...degraded.entries()].map(([tenantId, reason]) => ({ tenantId, reason }));
  const reason = tenants.length === 1
    ? tenants[0].reason
    : `${tenants.length} workspaces are not saving to this browser locally (the API remains the source of truth): ${tenants.map((t) => t.tenantId).join(', ')}.`;
  return { degraded: true, tenants, reason };
}

// Single setItem for one tenant's slice — atomic: either the whole chunk lands
// or (on quota) none of it does. A partially-written chunk would be worse than
// the previous good one, so a failure removes it rather than leaving it stale.
function persistTenant(db, tenantId) {
  const factSheets = db.factSheets.filter((fs) => fs.tenantId === tenantId);
  const prefix = `${tenantId}:`;
  const fieldDefs = {};
  Object.keys(db.fieldDefs || {}).forEach((k) => {
    if (k.startsWith(prefix)) fieldDefs[k] = db.fieldDefs[k];
  });
  try {
    localStorage.setItem(chunkKey(tenantId), JSON.stringify({ factSheets, fieldDefs }));
    degraded.delete(tenantId);
    return true;
  } catch (e) {
    try { localStorage.removeItem(chunkKey(tenantId)); } catch (e2) { /* ignore */ }
    degraded.set(tenantId, isQuotaError(e)
      ? `${factSheets.length} fact sheet(s) in this workspace no longer fit in this browser's local storage. It is staying loaded from memory for this session — nothing is lost, but it will not survive a reload without the API.`
      : `Local save failed for this workspace (${(e && e.message) || 'unknown error'}); it is staying loaded from memory for this session.`);
    return false;
  }
}

function persistManifest(db) {
  const manifest = {
    version: 4,
    tenantIds: [...knownTenantIds].sort(),
    catalog: db.catalog,
    seededAt: db.seededAt,
  };
  try {
    localStorage.setItem(MANIFEST_KEY, JSON.stringify(manifest));
  } catch (e) { /* manifest is small (ids + catalog); a failure here is not the quota story */ }
}

// Only rewrite the manifest when the set of persisted tenants actually moved —
// an ordinary save to an already-known tenant touches nothing but that
// tenant's own chunk.
function reconcileManifestIfNeeded(db, affectedTenantIds) {
  let changed = false;
  affectedTenantIds.forEach((id) => {
    const nowKnown = !degraded.has(id) && localStorage.getItem(chunkKey(id)) != null;
    if (nowKnown && !knownTenantIds.has(id)) { knownTenantIds.add(id); changed = true; }
    if (!nowKnown && knownTenantIds.has(id)) { knownTenantIds.delete(id); changed = true; }
  });
  if (changed) persistManifest(db);
}

function load() {
  if (cache) return cache;

  // 1. Current chunked format.
  const manifest = readJSON(MANIFEST_KEY);
  if (manifest && Array.isArray(manifest.tenantIds)) {
    const factSheets = [];
    const fieldDefs = {};
    manifest.tenantIds.forEach((id) => {
      knownTenantIds.add(id);
      const chunk = readJSON(chunkKey(id));
      if (chunk) {
        if (Array.isArray(chunk.factSheets)) factSheets.push(...chunk.factSheets);
        Object.assign(fieldDefs, chunk.fieldDefs || {});
      }
    });
    cache = freezeRows({
      factSheets,
      catalog: manifest.catalog || CATALOG,
      fieldDefs,
      seededAt: manifest.seededAt || '2026-08-12',
    });
    return cache;
  }

  // 2. No manifest yet — migrate the old single-blob key if it exists, so
  // nobody loses locally-held work just because the layout changed. Read once,
  // split into the new chunks, then remove it: this branch only ever runs once
  // per browser.
  const legacy = readJSON(LEGACY_LS_KEY);
  if (legacy) {
    cache = freezeRows({
      factSheets: Array.isArray(legacy.factSheets) ? legacy.factSheets : [],
      catalog: legacy.catalog || CATALOG,
      fieldDefs: legacy.fieldDefs || {},
      seededAt: legacy.seededAt || '2026-08-12',
    });
    save(cache);
    try { localStorage.removeItem(LEGACY_LS_KEY); } catch (e) { /* ignore */ }
    return cache;
  }

  // 3. Cold start — seed.
  cache = freezeRows({ factSheets: MERGED_FACT_SHEETS.map((f) => ({ ...f })), catalog: CATALOG, fieldDefs: {}, seededAt: '2026-08-12' });
  save(cache);
  return cache;
}

// `tenantHint`: the tenant this particular change belongs to, when the caller
// knows it (every real write path does). Only that tenant's chunk is written.
// Omit it for a genuinely cross-tenant resync (initial load, migration, a
// master fetch spanning workspaces) and every tenant present in `db` is
// persisted — still one setItem each, never one giant write.
function save(db, tenantHint) {
  cache = freezeRows(db);
  if (tenantHint && batchingTenants.has(tenantHint)) {
    dirtyBatchedTenants.add(tenantHint);
    return;
  }
  const affected = tenantHint
    ? [tenantHint]
    : [...new Set(db.factSheets.map((fs) => fs.tenantId))];
  affected.forEach((id) => persistTenant(db, id));
  reconcileManifestIfNeeded(db, affected);
}

// Removes every persisted chunk this session knows about, plus the legacy key
// defensively. Used only by resetToSeed, which is about to write a fresh set.
function clearAllPersisted() {
  knownTenantIds.forEach((id) => { try { localStorage.removeItem(chunkKey(id)); } catch (e) { /* ignore */ } });
  knownTenantIds.clear();
  degraded.clear();
  try { localStorage.removeItem(MANIFEST_KEY); } catch (e) { /* ignore */ }
  try { localStorage.removeItem(LEGACY_LS_KEY); } catch (e) { /* ignore */ }
}

function apiPut(fs) {
  if (!apiUp || !fs) return;
  fetch(`${apiBase()}/factsheet`, { method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(fs) }).catch(() => {});
}
function apiDel(fs) {
  if (!apiUp || !fs) return;
  fetch(`${apiBase()}/factsheet?id=${encodeURIComponent(fs.id)}&tenantId=${encodeURIComponent(fs.tenantId)}`,
    { method: 'DELETE', headers: authHeaders() }).catch(() => {});
}
// Write a custom field definition through to the database. Without this the
// definition existed only in the browser that created it, while the dialog told
// the user it applied to every fact sheet in the workspace — and it died on the
// next `resetToSeed()`. Best-effort in the same way `apiPut`/`apiDel` are: the
// local definition is what the current session renders either way.
//
// Note the endpoint is admin-only, so a non-admin's field stays local to their
// browser. The tenant is NOT sent — the API derives it from the verified token,
// which is what keeps a client from publishing a field globally.
function apiFieldDef(type, def) {
  if (!apiUp || !def || !def.key) return;
  fetch(`${apiBase()}/reference/field-def`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      key: def.key,
      label: def.label || def.key,
      entityType: type,
      dataType: def.type || 'text',
      section: def.section || 'Custom Fields',
      mandatory: !!def.mandatory,
      sortOrder: def.sortOrder || 0,
    }),
  }).catch(() => {});
}

async function apiSeed(factSheets) {
  try {
    await fetch(`${apiBase()}/seed`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ factSheets }) });
  } catch (e) { /* ignore */ }
}

// Hydrate the cache from the API (DynamoDB). Called once after config + auth are
// ready. Empty table → seed it. API unreachable → stay on localStorage so local
// development still works with no backend.
export async function initStore({ seedIfEmpty = true, tenantId } = {}) {
  const db = load();
  try {
    // ONE WORKSPACE AT A TIME. A master's unscoped list is every client's estate
    // at once — over 7,700 fact sheets — which exceeds what a single response can
    // carry. It failed with a 500, this catch treated that as "API down", and the
    // app fell back to seeded local data: a master would sit looking at invented
    // records believing they were real. Silently showing fake data is far worse
    // than showing an error, so the request is now always scoped.
    // Page through the workspace. The largest client holds 7,305 servers, which
    // is several times what one response can carry, so a single request for a
    // whole workspace fails — and a failure here is indistinguishable from the
    // API being down, which is how a full estate ends up replaced by seed data.
    const list = [];
    let offset = 0;
    const LIMIT = 400;
    for (let guard = 0; guard < 200; guard += 1) {   // hard stop; 80,000 rows
      const qs = new URLSearchParams({ paged: '1', limit: String(LIMIT), offset: String(offset) });
      if (tenantId) qs.set('tenantId', tenantId);
      // eslint-disable-next-line no-await-in-loop
      const res = await fetch(`${apiBase()}/factsheets?${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`status ${res.status}`);
      // eslint-disable-next-line no-await-in-loop
      const page = await res.json();
      const items = Array.isArray(page) ? page : (page.items || []);
      list.push(...items);
      if (Array.isArray(page) || !page.hasMore || !items.length) break;
      offset += LIMIT;
    }
    apiUp = true;
    if (Array.isArray(list) && list.length) {
      // Replace only the requested workspace so switching tenants accumulates
      // rather than discarding what was already loaded.
      db.factSheets = tenantId
        ? [...db.factSheets.filter((f) => f.tenantId !== tenantId), ...list]
        : list;
      save(db, tenantId);
    } else if (tenantId) {
      // An EMPTY but successful result used to fall through both branches: the
      // first required list.length, the second required !tenantId. So a
      // workspace whose records had all been deleted kept rendering the
      // previous session's localStorage rows as though they were live. An empty
      // answer from a reachable API is a fact, not a non-event.
      db.factSheets = db.factSheets.filter((f) => f.tenantId !== tenantId);
      save(db, tenantId);
    } else if (seedIfEmpty) { await apiSeed(db.factSheets); }
  } catch (e) { apiUp = false; }

  // Fields an administrator has added live in the database, but schema.js is
  // synchronous and cannot fetch them, so they are registered here once. Skip
  // this and a field can be created, stored, and then never appear on a single
  // fact sheet — the definition exists and the product silently ignores it.
  //
  // A master admin's field is stored with no tenant and therefore applies to
  // EVERY workspace; a field added inside a client workspace stays local to it.
  // The API already returns exactly the set the caller is entitled to see.
  try {
    const ref = await fetch(`${apiBase()}/reference`, { headers: authHeaders() });
    if (ref.ok) {
      const data = await ref.json();
      registerFieldDefs(data.fieldDefs || []);
    }
  } catch (e) { /* base schema still renders; added fields simply are not there */ }

  return { apiUp };
}

const clone = (x) => JSON.parse(JSON.stringify(x));
const nameCollator = new Intl.Collator();
const uid = (() => {
  let n = 0;
  return (p = 'fs') => `${p}-${Date.now().toString(36)}-${(n++).toString(36)}`;
})();

// ---- Completion scoring (LeanIX "% complete" / data quality) ---------------
// Fraction of non-relation fields that have a value, averaged with the mean of
// any explicit "% Complete - ..." percents already captured.
// A `fs.__completion` short-circuit used to sit inside this function. Nothing
// in the tree ever set that property, so it was dead — and a dead cache on the
// function that decides data quality is worse than none, because it reads like
// the memoisation this hot path actually needed. This is that memoisation,
// done where it can be reasoned about.
//
// The cache key is the fact sheet OBJECT, so it cannot go stale: the only thing
// that can change a completion score is a write, and every write path in this
// module replaces the row with a new object rather than mutating it — which the
// freeze above now enforces rather than merely assuming. A row that is still
// the same object is still the same data.
const COMPLETION = new WeakMap();

export function computeCompletion(fs) {
  if (!fs || typeof fs !== 'object') return 0;
  const cached = COMPLETION.get(fs);
  if (cached !== undefined) return cached;
  const value = completionOf(fs);
  COMPLETION.set(fs, value);
  return value;
}

// The completion RULE, in one place. A screen that scores a subset of fields —
// a survey covering four sections, say — must apply the same rule as the
// portfolio number, or the two disagree in front of the client: `SurveyWorkflow`
// counted a field answered "No" as captured while the fact-sheet badge beside it
// counted the same field as blank, and skipped the PERCENT blend entirely.
// Pass whichever field list you are scoring; the arithmetic is not yours to
// reimplement.
export function completionOver(fields, values) {
  const list = (fields || []).filter(
    (f) => f && f.type !== FIELD_TYPES.RELATION && f.type !== FIELD_TYPES.LIFECYCLE
  );
  if (!list.length) return 0;
  const bag = values || {};
  let filled = 0;
  const percents = [];
  for (const f of list) {
    const v = bag[f.key];
    if (f.type === FIELD_TYPES.PERCENT) {
      if (typeof v === 'number') percents.push(v);
      continue;
    }
    // `false` is an unanswered boolean, not a captured value.
    if (v !== undefined && v !== null && v !== '' && v !== false) filled += 1;
  }
  const base = Math.round((filled / list.length) * 100);
  if (percents.length) {
    const meanPct = percents.reduce((a, b) => a + b, 0) / percents.length;
    return Math.round(base * 0.5 + meanPct * 0.5);
  }
  return base;
}

function completionOf(fs) {
  return completionOver(allFieldsFor(fs.type), fs.fields);
}

// Merge custom field defs (per tenant+type) onto the base schema.
export function schemaWithCustom(db, tenantId, type) {
  const base = clone(schemaFor(type));
  const defs = (db.fieldDefs[`${tenantId}:${type}`] || []);
  if (defs.length) {
    // A custom field carries the SECTION it belongs in, and that has to be
    // honoured here. addFieldDef has always accepted and stored one, but every
    // def was being swept into a single "Custom Fields" bucket regardless --
    // so the way to deepen an existing area (Ownership, Migration Knowledge,
    // Classification) by adding a field to it did not work, and the field
    // landed in an unrelated pile at the bottom instead.
    let custom = null;
    const ensureCustom = () => {
      if (custom) return custom;
      custom = base.sections.find((x) => x.key === 'custom');
      if (!custom) {
        custom = { key: 'custom', label: 'Custom Fields', fields: [] };
        base.sections.push(custom);
      }
      return custom;
    };
    defs.forEach((def) => {
      // Match on key first, then label: the section is stored as free text and
      // older rows recorded the human label rather than the key.
      const want = def.section || 'custom';
      const target = base.sections.find((x) => x.key === want)
        || base.sections.find((x) => x.label === want);
      // An unknown section falls back to Custom Fields rather than being
      // dropped. A field the user can no longer see is worse than one in the
      // wrong place, and section names can be edited out from under a def.
      (target || ensureCustom()).fields.push(def);
    });
  }
  return base;
}

// ---------------------------------------------------------------------------
// Public async API
// ---------------------------------------------------------------------------
export const store = {
  // Returned the hardcoded ALL_TENANTS constant and never called the API, so a
  // customer created in CustomerAdmin never appeared in the switcher — it was
  // saved, and then invisible. The constant stays as the offline fallback: the
  // switcher must still render if the API is unreachable, and /customers is now
  // tenant-scoped, so a client user correctly gets just their own row back.
  async listTenants() {
    try {
      const res = await fetch(`${apiBase()}/customers`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.items || []);
        if (list.length) {
          return list.map((t) => ({
            id: t.id,
            name: t.name,
            kind: t.kind || 'client',
            status: t.status || 'active',
            color: t.color || undefined,
            logoUrl: t.logoUrl || t.logo_url || undefined,
            archivedAt: t.archivedAt || t.archived_at || null,
          }));
        }
      }
    } catch (e) { /* fall through to the bundled list */ }
    return clone(ALL_TENANTS);
  },

  // Which workspaces the master view is ACTUALLY built from. The app loads one
  // workspace at a time on purpose — an unscoped fetch of every client exceeds
  // the response cap and used to fail into seed data — so `optimum` shows the
  // union of whatever has been loaded this session, not the whole estate. The
  // banner claimed "all client portals", which is a promise this cannot keep.
  async loadedTenantIds() {
    const db = load();
    return [...new Set(db.factSheets.map((fs) => fs.tenantId).filter(Boolean))].sort();
  },

  // Master tenant ("optimum") sees everything; a client tenant sees only its own.
  async listFactSheets(tenantId, { type, search, tags, lifecycle } = {}) {
    const db = load();
    let rows = db.factSheets.filter((fs) => tenantId === 'optimum' || fs.tenantId === tenantId);
    if (type) rows = rows.filter((fs) => fs.type === type);
    if (lifecycle) rows = rows.filter((fs) => (fs.fields?.lifecycleState || '') === lifecycle);
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (fs) =>
          fs.name.toLowerCase().includes(q) ||
          (fs.fields?.description || '').toLowerCase().includes(q)
      );
    }
    if (tags && tags.length) {
      rows = rows.filter((fs) =>
        tags.every((t) => (fs.tags || []).some((x) => `${x.group}:${x.value}` === t))
      );
    }
    // A shallow copy, not a deep clone: the stored rows are frozen, so a copy
    // is all the isolation a caller can act on. See `deepFreeze` above.
    // `localeCompare` builds a collator per comparison — at 4,000 rows that is
    // ~48,000 of them per call, which cost more than the completion pass; one
    // shared collator does the same job.
    return rows
      .map((fs) => ({ ...fs, completion: computeCompletion(fs) }))
      .sort((a, b) => nameCollator.compare(a.name || '', b.name || ''));
  },

  async getFactSheet(id) {
    const db = load();
    const fs = db.factSheets.find((x) => x.id === id);
    if (!fs) return null;
    const resolved = clone(fs);
    resolved.completion = computeCompletion(fs);
    // resolve relation targets to {id, name, type} for display
    resolved.resolvedRelations = {};
    for (const [relKey, arr] of Object.entries(fs.relations || {})) {
      resolved.resolvedRelations[relKey] = (arr || [])
        .map((r) => {
          const t = db.factSheets.find((x) => x.id === r.id);
          return t ? { id: t.id, name: t.name, type: t.type } : null;
        })
        .filter(Boolean);
    }
    return resolved;
  },

  async saveFactSheet(fs) {
    const db = load();
    const idx = db.factSheets.findIndex((x) => x.id === fs.id);
    const record = clone(fs);
    // `completion` was deleted here before the write-through, so the API stored
    // 0 for every new fact sheet and a stale value for updates. The React app
    // never noticed because listFactSheets recomputes on read — but any other
    // consumer (a BI export, a report job, a second client) reads the stored
    // number, and it was wrong. Recompute and send it rather than dropping it.
    delete record.resolvedRelations;
    record.completion = computeCompletion(record);
    // Keep the top-level name (used by lists/headers/relations) in sync with
    // the editable fields.name.
    if (record.fields && record.fields.name) record.name = record.fields.name;
    if (idx >= 0) db.factSheets[idx] = { ...db.factSheets[idx], ...record };
    else {
      record.id = record.id || uid(record.type?.toLowerCase());
      db.factSheets.push(record);
    }
    save(db, record.tenantId);
    apiPut(idx >= 0 ? db.factSheets[idx] : record); // write-through to DynamoDB
    return this.getFactSheet(record.id);
  },

  async createFactSheet(tenantId, type, name) {
    return this.saveFactSheet({
      id: uid(type.toLowerCase()),
      type,
      tenantId: tenantId === 'optimum' ? 'optimum' : tenantId,
      name,
      fields: { name, lifecycleState: 'Plan' },
      tags: [],
      relations: {},
      quality: 'Draft',
    });
  },

  async deleteFactSheet(id) {
    const db = load();
    const fs = db.factSheets.find((x) => x.id === id);
    if (!fs) return; // nothing to delete, nothing to persist
    db.factSheets = db.factSheets.filter((x) => x.id !== id);
    save(db, fs.tenantId);
    apiDel(fs); // write-through delete to DynamoDB
  },

  // ---- Custom field defs (the flexible-schema superpower) ------------------
  async listFieldDefs(tenantId, type) {
    const db = load();
    return clone(db.fieldDefs[`${tenantId}:${type}`] || []);
  },

  // Adding the same field twice is a correction, not a second field. This used
  // to push blindly, so re-importing a file three times left three identical
  // `cf_*` entries on the Application schema and every caller had to carry its
  // own dedupe (only ImportDialog did).
  async addFieldDef(tenantId, type, def) {
    const db = load();
    const k = `${tenantId}:${type}`;
    const list = db.fieldDefs[k] || [];
    const record = { ...def, custom: true };
    const idx = list.findIndex((d) => d && d.key === def.key);
    if (idx >= 0) list[idx] = { ...list[idx], ...record };
    else list.push(record);
    db.fieldDefs[k] = list;
    save(db, tenantId);
    apiFieldDef(type, def);
    return clone(db.fieldDefs[k]);
  },

  async schemaFor(tenantId, type) {
    return schemaWithCustom(load(), tenantId, type);
  },

  // ---- Master Catalog (Optimum-owned reference library) -------------------
  // The canonical app definitions, shared across engagements. Only the master
  // workspace should surface adoption across clients.
  async listCatalog() {
    const db = load();
    return clone(db.catalog || []);
  },

  // For each catalog entry, which client tenants have an instance linked to it.
  async catalogAdoption() {
    const db = load();
    const tenantsById = Object.fromEntries(TENANTS.map((t) => [t.id, t]));
    return (db.catalog || []).map((c) => {
      const instances = db.factSheets.filter((fs) => fs.fields?.catalogId === c.id);
      const clientIds = [...new Set(instances.map((fs) => fs.tenantId))];
      const versions = [...new Set(instances.map((fs) => fs.fields?.version).filter(Boolean))];
      return {
        ...clone(c),
        clientCount: clientIds.length,
        versions, // distinct deployed versions across clients
        clients: clientIds.map((id) => {
          const inst = instances.find((fs) => fs.tenantId === id);
          return { id, name: tenantsById[id]?.name || id, version: inst?.fields?.version || null, instanceId: inst?.id };
        }),
        instanceIds: instances.map((fs) => ({ id: fs.id, tenantId: fs.tenantId })),
      };
    });
  },

  // Onboard a client app FROM the catalog: inherit canonical bits, keep it in
  // the client's own tenant (isolated). This is "don't reinvent the wheel".
  async createFromCatalog(tenantId, catalogId) {
    const db = load();
    const c = (db.catalog || []).find((x) => x.id === catalogId);
    if (!c) return null;
    return this.saveFactSheet({
      id: uid('app'),
      type: 'Application',
      tenantId,
      name: c.name,
      fields: {
        name: c.name,
        catalogId: c.id,
        description: c.description,
        vendorName: c.vendor,
        hostingType: c.typicalHosting,
        timeClassification: c.defaultTime,
        sixRClassification: c.default6R,
        lifecycleState: 'Plan',
      },
      tags: [{ group: 'Cell', value: c.category }],
      relations: {},
      quality: 'Draft',
    });
  },

  // Reset to seed (handy during the demo) — also re-seeds DynamoDB.
  async resetToSeed() {
    clearAllPersisted();
    cache = null;
    const db = load();
    await apiSeed(db.factSheets);
  },

  // Sync read of the current degraded state — see `persistenceDegraded` above.
  persistenceDegraded() {
    return persistenceDegraded();
  },
};

export default store;
